import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'
import type { AccountInfo } from '@azure/msal-browser'

const CLIENT_ID = 'f4dd1b82-9a62-4837-9d70-ba3ae7871506'
const TENANT_ID = '993a785d-e6a6-4724-9d6e-4054bc5ec624'
const REDIRECT_URI = window.location.origin

const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
}

const loginRequest = {
  scopes: [`api://${CLIENT_ID}/access_as_user`],
}

let msalInstance: PublicClientApplication | null = null
let initPromise: Promise<PublicClientApplication> | null = null

export async function initAuth(): Promise<PublicClientApplication> {
  if (msalInstance) return msalInstance
  if (initPromise) return initPromise
  initPromise = (async () => {
    msalInstance = new PublicClientApplication(msalConfig)
    await msalInstance.initialize()
    await msalInstance.handleRedirectPromise()
    return msalInstance
  })()
  return initPromise
}

// Ensure MSAL is ready before checking auth state
export async function ensureAuth(): Promise<void> {
  if (!msalInstance) await initAuth()
}

export function getAccount(): AccountInfo | null {
  if (!msalInstance) return null
  const accounts = msalInstance.getAllAccounts()
  return accounts.length > 0 ? accounts[0] : null
}

export async function login(): Promise<void> {
  if (!msalInstance) await initAuth()
  await msalInstance!.loginRedirect(loginRequest)
}

export async function logout(): Promise<void> {
  if (!msalInstance) return
  const account = getAccount()
  await msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri: REDIRECT_URI,
  })
}

export async function getAccessToken(): Promise<string> {
  if (!msalInstance) await initAuth()
  const account = getAccount()
  if (!account) {
    throw new Error('No authenticated user')
  }

  try {
    const response = await msalInstance!.acquireTokenSilent({
      ...loginRequest,
      account,
    })
    return response.accessToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance!.acquireTokenRedirect(loginRequest)
      throw new Error('Redirecting for authentication...', { cause: error })
    }
    throw error
  }
}

export function isAuthenticated(): boolean {
  return getAccount() !== null
}

export function getUserName(): string {
  const account = getAccount()
  return account?.name || account?.username || 'Unknown User'
}

export function getUserEmail(): string {
  const account = getAccount()
  return account?.username || ''
}
